<script lang="ts">
  import type { CostRow } from '../lib/cost-row';

  interface Props {
    row: CostRow;
    isChild?: boolean;
    isOpen?: boolean;
    onToggle?: () => void;
    onOpen?: () => void;
  }

  let { row, isChild = false, isOpen = false, onToggle, onOpen }: Props = $props();

  // A visão por modelo agrega clientes diferentes: não há um gasto para abrir.
  const opens = $derived(Boolean(onOpen) && row.detailRoute !== null);
</script>

<tr class:parent={row.hasChildren} class:child={isChild}>
  <td>
    <span class="name-line">
      {#if row.hasChildren && onToggle}
        <button
          class="toggle"
          aria-expanded={isOpen}
          aria-label={isOpen ? `Fechar ${row.name}` : `Abrir ${row.name}`}
          onclick={onToggle}
        >
          <span class="chevron" class:open={isOpen} aria-hidden="true">›</span>
        </button>
      {/if}
      {#if opens}
        <button class="row-name link" class:origin-internal={row.isInternalRoutine} onclick={onOpen}>
          {row.name}
          {#if !row.isPriced}<span class="badge-unpriced">sem preço</span>{/if}
        </button>
      {:else}
        <span class="row-name" class:origin-internal={row.isInternalRoutine}>
          {row.name}
          {#if !row.isPriced}<span class="badge-unpriced">sem preço</span>{/if}
        </span>
      {/if}
    </span>
    {#if row.hasChildren}
      <span class="row-sub mono">{row.summaryLabel}</span>
    {:else if row.subtitle}
      <span class="row-sub mono">{row.subtitle}</span>
    {/if}
  </td>
  <td class="num">{row.callsLabel}</td>
  <td class="num strong">{row.averageLabel}</td>
  <td class="num">{row.cacheHitLabel}</td>
  <td class="num mono dim">{row.missLabel}</td>
  <td class="num mono dim">{row.hitLabel}</td>
  <td class="num mono dim">
    {row.outputLabel}
    <span class="row-sub">{row.reasoningLabel} raciocínio</span>
  </td>
  <td class="num total">
    <span class="total-brl">{row.brlLabel}</span>
    <span class="row-sub mono">{row.usdLabel} · {row.shareLabel}</span>
    <span class="share-track" aria-hidden="true">
      <span class="share-fill" style="width: {row.barPercent}%"></span>
    </span>
  </td>
</tr>

<style>
  tr:hover td {
    background: var(--bg-hover);
  }
  td {
    padding: var(--space-4);
    border-bottom: 1px solid var(--border-subtle);
    color: var(--text-secondary);
    vertical-align: top;
    transition: background var(--duration-fast) var(--easing-default);
  }
  tr.child td {
    background: var(--bg-elevated);
  }
  tr.child td:first-child {
    padding-left: var(--space-6);
  }
  .num {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
  .dim {
    opacity: 0.72;
    font-size: 0.92em;
  }
  .strong {
    color: var(--text-primary);
    font-weight: 600;
  }
  /* O real é o número que se lê; o dólar e a fatia ficam de apoio. */
  .total {
    position: relative;
  }
  .total-brl {
    display: block;
    color: var(--text-primary);
    font-weight: 600;
    font-size: 1.2em;
    letter-spacing: -0.01em;
  }
  /* A barra dá a proporção sem o olho ter que comparar números. O trilho existe
     para a barra curta continuar tendo contra o que ser lida. */
  .share-track {
    display: block;
    margin-top: 6px;
    margin-left: auto;
    width: 96px;
    height: 6px;
    border-radius: 3px;
    background: var(--border-subtle);
    overflow: hidden;
  }
  .share-fill {
    display: block;
    height: 100%;
    border-radius: 3px;
    background: linear-gradient(90deg, #2f9e63 0%, var(--color-success) 100%);
  }
  .toggle {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    background: none;
    border: none;
    padding: 0;
    color: inherit;
    font: inherit;
    cursor: pointer;
    text-align: left;
  }
  .toggle:hover .chevron {
    color: var(--text-primary);
  }
  .toggle:focus-visible {
    outline: 2px solid var(--color-success);
    outline-offset: 2px;
    border-radius: var(--radius-sm);
  }
  .chevron {
    display: inline-block;
    color: var(--text-tertiary);
    transition: transform var(--duration-fast) var(--easing-default);
  }
  .chevron.open {
    transform: rotate(90deg);
  }
  .name-line {
    display: flex;
    align-items: center;
    gap: var(--space-1);
  }
  .row-name {
    display: block;
    color: var(--text-primary);
    text-align: left;
  }
  .row-name.link {
    padding: 0;
    font: inherit;
    background: none;
    border: none;
    cursor: pointer;
    border-bottom: 1px solid transparent;
  }
  .row-name.link:hover {
    color: var(--color-success);
    border-bottom-color: var(--color-success);
  }
  .row-name.link:focus-visible {
    outline: 2px solid var(--color-success);
    outline-offset: 2px;
  }
  /* Rotina nossa, não atendimento de cliente. */
  .row-name.origin-internal {
    border-left: 2px solid var(--color-warning, #d9a441);
    padding-left: var(--space-2);
  }
  .row-sub {
    display: block;
    font-size: 11px;
    color: var(--text-tertiary);
    margin-top: 2px;
  }
  .num .row-sub {
    margin-top: 0;
  }
  .badge-unpriced {
    display: inline-block;
    margin-left: var(--space-2);
    padding: 1px 6px;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--color-warning, #d9a441);
    border: 1px solid var(--color-warning, #d9a441);
    border-radius: var(--radius-sm);
  }
</style>
